import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import { ChevronLeft, Users, Sparkles, Star, ChevronRight, Loader2 } from "lucide-react";

export default function CollabReviews() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("client_collab_reviews_title"));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/client/collab/reviews"); setItems(data?.items || []); }
    catch { setItems([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditor = (item, coach) => { setEditing({ item, coach }); setRating(coach.review?.rating || 5); setComment(coach.review?.comment || ""); };

  const submit = async () => {
    if (!editing || busy) return;
    setBusy(true);
    try {
      await api.post("/client/collab/reviews", {
        project_kind: editing.item.project_kind, project_id: editing.item.project_id,
        coach_id: editing.coach.id, rating, comment: comment.trim(),
      });
      setEditing(null);
      toast.success(t("client_collab_review_saved"));
      await load();
    } catch (e) { toast.error(apiError(e, t("error"))); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="client-collab-reviews">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/collab")} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("client_collab_reviews_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("client_collab_reviews_sub")}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("client_collab_reviews_empty")}</p>
      ) : items.map((it) => (
        <div key={`${it.project_kind}-${it.project_id}`} className="rounded-2xl border border-border bg-secondary/60 p-4">
          <div className="flex items-center gap-2 border-b border-border pb-2">
            {it.project_kind === "circle" ? <Users className="h-3.5 w-3.5 text-primary" /> : <Sparkles className="h-3.5 w-3.5 text-primary" />}
            <p className="truncate font-display text-sm text-foreground">{it.title}</p>
          </div>
          {it.coaches.map((c) => (
            <button key={c.id} data-testid={`review-row-${it.project_id}-${c.id}`} onClick={() => openEditor(it, c)} className="flex w-full items-center gap-3 py-2.5 text-left">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/20 text-xs font-semibold text-primary">{c.photo ? <img src={c.photo} alt="" className="h-full w-full object-cover" /> : (c.name || "?")[0]?.toUpperCase()}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-foreground">{c.name}</span>
                {c.review ? (
                  <span className="mt-0.5 flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className="h-3 w-3" fill={n <= c.review.rating ? "#F59E0B" : "none"} style={{ color: "#F59E0B" }} />)}
                    {c.review.comment ? <span className="ml-1 truncate text-xs text-muted-foreground">{c.review.comment}</span> : null}
                  </span>
                ) : <span className="mt-0.5 block text-xs font-medium text-primary">{t("client_collab_review_add")}</span>}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      ))}

      {editing ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-2xl bg-background p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-lg text-foreground">{t("client_collab_review_editor_title", { coach: editing.coach?.name || "" })}</h2>
            <div className="my-4 flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)}><Star className="h-8 w-8" fill={n <= rating ? "#F59E0B" : "none"} style={{ color: "#F59E0B" }} /></button>
              ))}
            </div>
            <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} placeholder={t("client_collab_review_comment_ph")} className="min-h-[80px] w-full rounded-lg bg-secondary/60 px-3 py-2 text-sm text-foreground outline-none" />
            <button data-testid="review-submit" onClick={submit} disabled={busy} className="mt-3 flex w-full items-center justify-center rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("client_collab_review_submit")}
            </button>
            <button onClick={() => setEditing(null)} className="mt-2 w-full py-2 text-sm text-muted-foreground">{t("cancel")}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
